"use client";

import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { AnimatedHeadline } from "@/components/ui/animated-headline";
import { HeroOrbitGraphic } from "@/components/home/hero-orbit-graphic";
import { useCursor } from "@/providers/cursor-provider";

const HeroScene = dynamic(
  () => import("@/components/three/hero-scene").then((m) => m.HeroScene),
  {
    ssr: false,
    loading: () => null,
  },
);

const capabilities = ["Generative AI", "Agentic Systems", "Custom Software", "Cloud & Data"] as const;

export function Hero() {
  const mouse = useRef({ x: 0, y: 0 });
  const { setLabel } = useCursor();

  return (
    <section
      className="relative isolate w-full overflow-hidden lg:min-h-[100svh]"
      onMouseEnter={() => setLabel("EXPLORE")}
      onMouseLeave={() => setLabel(null)}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouse.current = {
          x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
          y: ((e.clientY - rect.top) / rect.height) * 2 - 1,
        };
      }}
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 opacity-[0.34] [mask-image:linear-gradient(105deg,transparent_0%,transparent_18%,black_52%)] dark:opacity-50 dark:[mask-image:none]">
          <HeroScene mouse={mouse} />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_72%_42%,var(--hero-glow-1),transparent_52%),radial-gradient(ellipse_at_16%_28%,var(--hero-glow-2),transparent_48%),radial-gradient(ellipse_at_58%_88%,var(--hero-glow-3),transparent_52%)]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background from-[10%] via-background/82 via-[40%] to-transparent to-[76%] dark:from-[color-mix(in_srgb,var(--background)_92%,transparent)] dark:via-[color-mix(in_srgb,var(--background)_55%,transparent)]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-background/75 to-transparent" />
      </div>

      <div className="relative z-10 pt-24 pb-16 sm:pt-28 sm:pb-20 lg:flex lg:min-h-[100svh] lg:items-center lg:pb-24">
        <div className="container-wide grid w-full items-start gap-10 sm:gap-12 lg:grid-cols-[1fr_1.08fr] lg:items-center lg:gap-8 xl:gap-12">
          <div className="relative z-20 max-w-xl">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="eyebrow mb-5"
            >
              Maxridge · AI-first engineering
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
            >
              <AnimatedHeadline
                className="text-[clamp(1.9rem,6.4vw,3.55rem)] text-foreground"
                lines={["AI DEVELOPMENT &", "SOFTWARE ENGINEERING", "COMPANY"]}
                as="h1"
              />
              <span className="sr-only">
                MaxRidge — AI development and software engineering company in Noida, India
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.22 }}
              className="mt-6 max-w-lg text-[15px] leading-7 text-muted sm:text-base sm:leading-8"
            >
              AI-first engineering from MaxRidge in Noida — generative AI, AI agents, custom software,
              cloud, data platforms, and dedicated development teams for clients worldwide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.32 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <Button
                href="/contact"
                size="md"
                variant="secondary"
                arrow
                cursorLabel="START"
                className="w-full sm:w-auto"
              >
                Book a Free Consultation
              </Button>
              <Button
                href="#capabilities"
                size="md"
                variant="outline"
                cursorLabel="EXPLORE"
                className="w-full sm:w-auto"
              >
                Explore Services
              </Button>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.42 }}
              className="mt-9 flex flex-wrap gap-2.5"
            >
              {capabilities.map((item, i) => (
                <li
                  key={item}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card/80 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/80 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_20px_rgba(11,40,90,0.06)] backdrop-blur-md dark:bg-white/5 dark:shadow-none"
                >
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-[1px]"
                    style={{
                      background:
                        i % 3 === 0
                          ? "var(--accent)"
                          : i % 3 === 1
                            ? "var(--cyan)"
                            : "var(--accent-tertiary)",
                      boxShadow: `0 0 10px ${
                        i % 3 === 0
                          ? "var(--accent)"
                          : i % 3 === 1
                            ? "var(--cyan)"
                            : "var(--accent-tertiary)"
                      }`,
                    }}
                  />
                  {item}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mx-auto mt-2 aspect-square w-full max-w-[18rem] shrink-0 sm:mt-4 sm:max-w-[25rem] md:max-w-[30rem] lg:mt-0 lg:max-w-none"
          >
            <div className="pointer-events-none absolute inset-[8%] bg-[radial-gradient(ellipse_at_center,color-mix(in_srgb,var(--accent)_18%,transparent),transparent_68%)] blur-2xl dark:opacity-80" />
            <HeroOrbitGraphic className="absolute inset-0" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
